import { createReducer, on } from '@ngrx/store';

import { OpraSessionService } from '@shared/services';
import { IUserSession } from '@shared/types/session';

import {
    check_user_auth,
    check_user_auth_failure,
    check_user_auth_success,
    fetch_companies,
    fetch_companies_failure,
    fetch_companies_success,
    fetch_company_info,
    fetch_company_info_failure,
    fetch_company_info_success,
    get_access_token,
    get_access_token_failure,
    get_access_token_for_no_iframe,
    get_access_token_for_no_iframe_failure,
    get_access_token_for_no_iframe_success,
    get_access_token_success,
    get_company_lang,
    get_company_lang_failure,
    get_company_lang_success,
    get_company_roles,
    get_company_roles_failure,
    get_company_roles_success,
    get_permissions,
    get_permissions_failure,
    get_permissions_success,
    get_user,
    get_user_failure,
    get_user_success,
    hide_company_selector,
    local_check_user_auth,
    local_check_user_auth_success,
    log_out,
    restore_token,
    restore_token_failure,
    restore_token_success,
    set_iframe_mode,
    set_session_ready,
    set_token,
    show_company_selector,
    user_login,
    user_login_failure,
    user_login_success,
} from '../actions/session.action';
import { add_sites_success } from '../actions/sites.action';

export const initialState: IUserSession = {
    user: {
        person_id: null,
        email: null,
        name: null,
        id: null,
        preferred_language: 'en-US',
        roles: [],
    },
    token: null,
    accessTokenSet: false,
    companies: [],
    isAuthenticated: false,
    companyRoles: [],
    showCompanySelector: false,
    currentCompanyId: null,
    isIframeMode: false,
    userPermissions: { total: 0, items: [] },
    reports: [],
    company_lang: 'en-US',
    session_ready: false,
};

const _sessionReducer = createReducer(
    initialState,

    on(get_user, (state): IUserSession => state),
    on(get_user_success, (state, { user }): IUserSession => ({ ...state, user })),
    on(get_user_failure, (state): IUserSession => ({ ...state, user: null })),

    on(user_login, (state): IUserSession => state),
    on(user_login_success, (state, { token }): IUserSession => ({ ...state, token, isAuthenticated: true })),
    on(user_login_failure, (state): IUserSession => ({ ...state, token: null, isAuthenticated: false })),

    on(check_user_auth, (state): IUserSession => state),
    on(
        check_user_auth_success,
        (state, { user, token, companyId }): IUserSession => ({
            ...state,
            user,
            token,
            isAuthenticated: true,
            currentCompanyId: companyId,
        }),
    ),

    on(local_check_user_auth, (state): IUserSession => state),
    on(
        local_check_user_auth_success,
        (state, { user, token, companyId }): IUserSession => ({
            ...state,
            user,
            token,
            isAuthenticated: true,
            currentCompanyId: companyId,
        }),
    ),
    on(check_user_auth_failure, (state): IUserSession => ({ ...state, token: null, isAuthenticated: false })),

    on(fetch_companies, (state): IUserSession => state),
    on(
        fetch_companies_success,
        (state, { items }): IUserSession => ({
            ...state,
            companies: [...items],
            showCompanySelector: !state.isIframeMode,
        }),
    ),
    on(fetch_companies_failure, (state): IUserSession => ({ ...state, companies: [], showCompanySelector: false })),

    on(
        set_token,
        (state, { token }): IUserSession => ({
            ...state,
            token,
            currentCompanyId: OpraSessionService.parseToken(token).user.company_id,
        }),
    ),
    on(set_iframe_mode, (state, { isIframeMode }): IUserSession => ({ ...state, isIframeMode })),

    on(get_access_token, (state, { companyId }): IUserSession => ({ ...state, currentCompanyId: companyId })),
    on(get_access_token_success, (state, { token }): IUserSession => {
        const companyId = OpraSessionService.parseToken(token).user.company_id;
        return { ...state, token, accessTokenSet: true, currentCompanyId: companyId };
    }),
    on(get_access_token_failure, (state): IUserSession => state),

    on(
        get_access_token_for_no_iframe,
        (state, { companyId }): IUserSession => ({ ...state, currentCompanyId: companyId }),
    ),
    on(get_access_token_for_no_iframe_success, (state, { token, user }): IUserSession => {
        const companyId = OpraSessionService.parseToken(token).user.company_id;
        return { ...state, token, accessTokenSet: true, currentCompanyId: companyId, user, session_ready: true };
    }),
    on(get_access_token_for_no_iframe_failure, (state): IUserSession => state),

    on(get_company_roles, (state): IUserSession => state),
    on(get_company_roles_success, (state, { companyRoles }): IUserSession => ({ ...state, companyRoles })),
    on(get_company_roles_failure, (state): IUserSession => ({ ...state, companyRoles: [] })),

    on(get_company_lang, (state): IUserSession => state),
    on(
        get_company_lang_success,
        (state, { preferred_language }): IUserSession => ({ ...state, company_lang: preferred_language }),
    ),
    on(get_company_lang_failure, (state): IUserSession => ({ ...state, company_lang: 'en-US' })),

    on(get_permissions, (state): IUserSession => state),
    on(get_permissions_success, (state, { permissions }): IUserSession => ({ ...state, userPermissions: permissions })),
    on(get_permissions_failure, (state): IUserSession => ({ ...state, userPermissions: { total: 0, items: [] } })),
    on(log_out, (): IUserSession => initialState),

    on(show_company_selector, (state): IUserSession => ({ ...state, showCompanySelector: true })),
    on(hide_company_selector, (state): IUserSession => ({ ...state, showCompanySelector: false })),

    on(add_sites_success, (state): IUserSession => ({ ...state, showCompanySelector: false })),

    on(restore_token, (state): IUserSession => state),
    on(restore_token_failure, (state): IUserSession => ({ ...state })),
    on(restore_token_success, (state, { token }): IUserSession => {
        const companyId = OpraSessionService.parseToken(token).user.company_id;
        return {
            ...state,
            token,
            currentCompanyId: companyId,
            isAuthenticated: true,
            isIframeMode: true,
            accessTokenSet: true,
        };
    }),

    on(set_session_ready, (state): IUserSession => ({ ...state, session_ready: true })),

    on(fetch_company_info, (state): IUserSession => state),
    on(fetch_company_info_success, (state, { items }): IUserSession => ({ ...state, companies: [...items] })),
    on(fetch_company_info_failure, (state): IUserSession => ({ ...state, companies: [] })),
);

export function sessionReducer(state, action) {
    return _sessionReducer(state, action);
}
